import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { BrandDot } from "@/components/util/BrandDot";
import { Reveal } from "@/components/util/Reveal";
import { ServiceIcon } from "@/components/home/ServiceIcon";
import { usePlayOnceInView } from "@/lib/usePlayOnceInView";

// Phase 2 CMS Reconciliation (Feb 2026) — homepage finder tiles now come
// from `services[].finder_items[]` (fields: key, label, icon_key,
// focus_param, show_on_home, show_on_services, order). Seeded by
// `migrate_finder_items_v1` and editable via the Service admin editor.
// No local HOME_TILES array anymore.
const flattenHomeFinderItems = (services) => {
  const rows = [];
  (services || []).forEach((svc) => {
    (svc.finder_items || []).forEach((item) => {
      if (!item?.show_on_home) return;
      rows.push({
        key: item.key || `${svc.slug}-${item.label}`,
        label: item.label || "",
        examples: item.examples || "",
        icon: item.icon_key ? `/finder-icons/${item.icon_key}.png` : "",
        iconKey: item.icon_key || "",
        to: item.href || `/services/${svc.slug}${item.focus_param ? `?focus=${item.focus_param}` : ""}`,
        order: Number.isFinite(item.order) ? item.order : 0,
      });
    });
  });
  rows.sort((a, b) => a.order - b.order);
  return rows;
};

export const ServicesSection = ({ services, showDot = false, eyebrow, heading, headingAccent, accentInline = false, description }) => {
  const eyebrowText = eyebrow || "What We Do";
  const headingText = heading || "From a Complete Brand to a Single Label.";
  const descriptionText = description || "Build a complete brand from scratch, or get one piece designed right — a menu, a label, a campaign. Every project gets the same studio standard.";

  const homeTiles = flattenHomeFinderItems(services);

  // Sep 2026 icon micro-animations — touch devices play each icon ONCE at
  // ~35% card visibility (staggered 100ms). Desktop hover is pure CSS.
  const { played, register } = usePlayOnceInView(homeTiles.map((t) => t.key).join("|"));

  return (
    <section data-testid="services-section" className="bg-[#0F0F0F] text-white pt-24 pb-16 sm:pb-20">
      <div className="max-w-[1400px] min-[1440px]:max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 min-[1440px]:px-[clamp(2.5rem,4vw,4rem)]">
        <Reveal className="text-center max-w-3xl lg:max-w-4xl min-[1440px]:max-w-5xl mx-auto">
          <div data-testid="services-section-eyebrow" className="ur-eyebrow">{eyebrowText}</div>
          <h2 data-testid="services-section-heading" className="font-display mt-4 font-black text-center text-[clamp(46px,13.5vw,54px)] leading-[0.95] tracking-[-0.02em] sm:text-6xl sm:leading-[0.95] lg:text-[72px] min-[1440px]:text-[84px]">
            {headingText}
            {headingAccent && (
              accentInline
                ? <span className="text-[#FF6600]">{headingAccent}</span>
                : <> <span className="text-[#FF6600]">{headingAccent}</span></>
            )}
          </h2>
          {showDot && <BrandDot testId="services-section-dot" />}
          {descriptionText && (
            <p data-testid="services-section-description" className="mt-5 text-[#A3A3A3] text-base sm:text-lg leading-relaxed">
              {descriptionText}
            </p>
          )}
        </Reveal>

        {/* 6 public category shortcuts (Sep 2026 six-category catalog):
              • base (mobile) → 2-column via flex-wrap → 2+2+2.
              • sm            → 3-column flex-wrap    → 3+3.
              • lg+ (desktop) → explicit 3 + 3, each row independently
                                centered on the page-center axis using
                                the same `display: contents` pattern
                                proven on /services.
            Tile targets (Feb 2026 refinement pass): ~172px mobile,
            ~215px desktop — tighter than the old photographic cards so
            these read as navigation shortcuts, not content cards. Icon
            sizes are kept at their approved absolute values (70/80px)
            so the artwork does NOT shrink with the card. */}
        <Reveal delay={80}>
          <div
            data-testid="home-services-finder"
            className="mt-10 sm:mt-12 flex flex-wrap justify-center gap-3 sm:gap-4 lg:flex-col lg:gap-5"
          >
            {(homeTiles.length <= 5 ? [homeTiles] : [homeTiles.slice(0, 3), homeTiles.slice(3)]).map((row, rowIdx) => (
              <div
                key={`home-finder-row-${rowIdx + 1}`}
                data-testid={`home-services-finder-row-${rowIdx + 1}`}
                // At <lg the wrapper is `contents` so all 7 tiles flow
                // inside the outer flex container. At lg+ each wrapper
                // becomes its own flex-wrap-center row → clean 4 + 3.
                className="contents lg:flex lg:flex-wrap lg:justify-center lg:gap-5"
              >
                {row.map((tile) => (
                  <Link
                    key={tile.key}
                    to={tile.to}
                    ref={register(tile.key)}
                    data-testid={`home-finder-tile-${tile.key}`}
                    // Card composition (Feb 2026 polish):
                    //   • justify-center vertically centers a
                    //     FIXED-HEIGHT content group — because the
                    //     icon slot and title slot both have
                    //     deterministic heights, every card produces
                    //     the same content height regardless of
                    //     whether the title wraps to 1 or 2 lines,
                    //     so icons and title zones align pixel-
                    //     perfect across the grid.
                    //   • Icons enlarged ~14% (70→80 desktop,
                    //     80→84 mobile).
                    //   • No dead space beneath the title; the
                    //     group sits optically centered inside the
                    //     aspect-square card.
                    //   • Mobile width is calc((100% - 12px) / 2)
                    //     capped at 172px so 2 tiles always share
                    //     a row down to 320px viewports (iPhone SE
                    //     class). Above sm the fixed pixel widths
                    //     kick back in for the desktop 4+3 layout.
                    //   • Explicit inline aspectRatio 1/1 guarantees
                    //     the tile is a true square on every mobile
                    //     device even where the Tailwind
                    //     `aspect-square` utility fails to apply
                    //     (some older Safari builds ignore it when
                    //     the parent uses `contents`).
                    style={{ aspectRatio: "1 / 1" }}
                    className="ur-home-tile group relative flex flex-col items-center justify-start sm:justify-center rounded-2xl border border-[#262626] bg-[#151515] hover:bg-[#191919] hover:border-[#FF6600]/60 aspect-square px-3 pt-4 pb-2 sm:pt-3 sm:pb-3 transition-all focus:outline-none focus:ring-2 focus:ring-[#FF6600]/50 w-[calc((100%-0.75rem)/2)] max-w-[172px] sm:w-[172px] sm:max-w-none md:w-[180px] lg:w-[215px]"
                  >
                    <span
                      className={`ur-home-tile__icon grid place-items-center h-[72px] w-[72px] sm:h-[88px] sm:w-[88px] shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5${played[tile.key] !== undefined ? " ur-svc-icon--play" : ""}`}
                      style={played[tile.key] !== undefined ? { "--ur-stagger": `${played[tile.key]}ms` } : undefined}
                    >
                      <ServiceIcon
                        iconKey={tile.iconKey}
                        fallbackSrc={tile.icon}
                        className="w-[62px] h-[62px] sm:w-[80px] sm:h-[80px]"
                      />
                    </span>
                    {/* Reserved title block — fixed min-height fits
                        two lines at text-sm/leading-tight so 1-line
                        and 2-line titles occupy the same vertical
                        footprint below the icon. */}
                    <div className="mt-1.5 sm:mt-3 w-full flex items-start justify-center text-center min-h-[2.4em] sm:min-h-[2.7em]">
                      <span className="block text-[12.5px] sm:text-sm font-semibold text-white leading-tight break-words">
                        {tile.label}
                      </span>
                    </div>
                    {tile.examples && (
                      <span
                        data-testid={`home-finder-tile-examples-${tile.key}`}
                        className="mt-1 block w-full text-center text-[10px] sm:text-[11px] leading-snug text-[#7A7A7A]"
                      >
                        {tile.examples}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </Reveal>

        {/* Single CTA (Oct 2026): only "View All Services" remains beneath
            the six category cards — the two explanatory text links were
            removed to eliminate competing calls to action. Both journeys
            live on elsewhere (hero CTAs, /services finder + not-sure
            callout, ServiceHelpLinks). */}
        <div className="mt-12 sm:mt-14 flex justify-center">
          <Link
            to="/services#services-finder"
            data-testid="view-all-services-cta"
            className="ur-btn-secondary ur-btn-secondary--fill"
          >
            View All Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// NOTE (Feb 2026): The homepage Subscription Design Services strip was
// intentionally retired. The `SubscriptionBlock` component and its CMS
// settings are kept in place — the underlying subscription offering,
// pricing, plans, routes and admin config are UNCHANGED — but the
// homepage no longer renders it beneath the Services Finder. If the
// strip is ever needed again in another surface, this component can be
// imported as-is.

const SUB_FEATURES_FALLBACK = [
  { icon: "RefreshCw", title: "Unlimited Requests", sub: "One at a time" },
  { icon: "Zap", title: "Fast Delivery", sub: "Priority turnaround" },
  { icon: "UserCheck", title: "Dedicated Designer", sub: "Consistent quality" },
  { icon: "Clock", title: "Flexible Plans", sub: "Cancel anytime" },
];

const SubscriptionBlock = () => {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  const eyebrow = s.subscription_eyebrow || "Recurring Value";
  const heading = s.subscription_heading || "Subscription Design Services";
  const headingAccent = s.subscription_heading_accent || "";
  const accentInline = !!s.subscription_accent_inline;
  const showDot = !!s.subscription_show_dot;
  const description = s.subscription_description || "Get ongoing design support for your business with flexible monthly plans. Pause or cancel anytime.";
  const ctaLabel = s.subscription_cta_label || "View Plans";
  const ctaLink = s.subscription_cta_link || "/services/website-design";
  const MainIcon = Icons[s.subscription_icon] || Crown;
  const features = (Array.isArray(s.subscription_features) && s.subscription_features.length > 0)
    ? s.subscription_features
    : SUB_FEATURES_FALLBACK;

  return (
    <div data-testid="subscription-block" className="mt-6 sm:mt-8 bg-[#141414] text-white rounded-2xl border border-[#262626] px-6 sm:px-8 py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-10">
      {/* Left: circle-outline crown + copy + button */}
      <div className="flex items-center gap-5 md:min-w-[380px]">
        <div className="w-20 h-20 rounded-full border-2 border-[#FF6600] grid place-items-center shrink-0">
          <MainIcon className="w-9 h-9 text-[#FF6600]" strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <div data-testid="subscription-eyebrow" className="text-[#FF6600] text-[10.5px] font-bold tracking-[0.2em] uppercase">{eyebrow}</div>
          <h3 data-testid="subscription-heading" className="font-display text-lg sm:text-xl font-bold mt-1 leading-tight">
            {heading}
            {headingAccent && (
              <span className="text-[#FF6600]">{accentInline ? headingAccent : ` ${headingAccent}`}</span>
            )}
          </h3>
          {showDot && <BrandDot size="sm" testId="subscription-brand-dot" className="!my-2 !justify-start" />}
          <p data-testid="subscription-description" className="mt-1 text-[13px] text-[#A3A3A3] leading-snug max-w-md">
            {description}
          </p>
          <Link
            to={ctaLink}
            data-testid="subscription-cta"
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#3A3A3A] text-white text-[11px] font-bold tracking-[0.18em] uppercase px-4 py-2 hover:border-[#FF6600] hover:text-[#FF6600] transition-colors"
          >
            {ctaLabel} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Right: bare feature icons */}
      <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-6">
        {features.map((f, i) => {
          const Icon = Icons[f.icon] || Icons.Sparkles;
          return (
            <div key={i} data-testid={`subscription-feature-${i}`} className="flex flex-col items-center text-center">
              <Icon className="w-8 h-8 text-[#FF6600] mb-2.5" strokeWidth={2} />
              <div className="text-sm font-bold leading-tight">{f.title}</div>
              <div className="text-xs text-[#A3A3A3] mt-0.5">{f.sub}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
